import { getBrowserApiBaseUrl } from '@/config/env'
import { getAuthTokenFromStorage } from '@/lib/utils/storage'
import type { BalanceReportListParams } from '../types/pages'
import type {
  OutstandingBalanceApplicationInfo,
  OutstandingBalancePaymentLogRow,
  OutstandingBalanceRow,
} from '../types/api'
import { DEFAULT_BALANCE_REPORT_LIST_PARAMS } from '../constants/balanceReportPage'
import { ymdToDmy } from '../utils/reportDateParams'
import { formatReportDate } from './reportDateDisplay'
import {
  readOutstandingBalanceSession,
  sessionFilterToOutstandingBalancePatch,
} from '../utils/outstandingBalanceSessionFilter'
import {
  LABEL_CLASS,
  FILTER_SELECT_CLASS as SELECT_CLASS,
} from '../constants/configuredReportStyles'
import {
  NESTED_PAYMENT_DELETE_EXCLUDED_INCOMPLETE_TYPES,
  NESTED_PAYMENT_DELETE_POLICY,
  NESTED_PAYMENT_DELETE_TYPES_WITHOUT_SOURCE,
  NESTED_PAYMENT_EDIT_POLICY,
  NESTED_PAYMENT_EDIT_TYPES_WITHOUT_SOURCE,
  NESTED_PAYMENT_EDIT_TYPES_WITH_SOURCE,
  NESTED_PAYMENT_EXTERNAL_SOURCE_MARKER,
  NESTED_PAYMENT_REJECTED_REFUND_TYPES,
  NESTED_PAYMENT_STATUS_CANCELLED,
  NESTED_PAYMENT_STATUS_COMPLETED,
  NESTED_PAYMENT_STATUS_REJECTED,
} from '../constants/balanceNestedPaymentActions'

export { LABEL_CLASS, SELECT_CLASS }

export function initialParams(): BalanceReportListParams {
  const session = readOutstandingBalanceSession()
  if (!session) return { ...DEFAULT_BALANCE_REPORT_LIST_PARAMS }
  return {
    ...DEFAULT_BALANCE_REPORT_LIST_PARAMS,
    ...sessionFilterToOutstandingBalancePatch(session),
  }
}

export function buildOutstandingQueryParams(p: BalanceReportListParams): URLSearchParams {
  const qs = new URLSearchParams()
  qs.set('page', String(p.page))
  qs.set('perPage', String(p.perPage))
  qs.set('sortBy', p.sortBy)
  qs.set('orderBy', p.orderBy)
  if (p.q.trim()) qs.set('q', p.q.trim())
  if (p.company_id) qs.set('company_id', p.company_id)
  if (p.property_id) qs.set('property_id', p.property_id)
  if (p.application_status) qs.set('application_status', p.application_status)
  if (p.amount_condition && p.payment_amount) {
    qs.set('amount_condition', p.amount_condition)
    qs.set('payment_amount', p.payment_amount)
  }
  if (p.chargeback_type) qs.set('chargeback_type', p.chargeback_type)
  const fromDt = ymdToDmy(p.fromDt)
  const toDt = ymdToDmy(p.toDt)
  if (fromDt) qs.set('fromDt', fromDt)
  if (toDt) qs.set('toDt', toDt)
  return qs
}

export function buildOutstandingExportHref(p: BalanceReportListParams): string {
  const token = getAuthTokenFromStorage()
  if (!token) return ''
  const qs = buildOutstandingQueryParams(p)
  qs.set('access_token', token)
  qs.set('view', 'xlsx')
  return `${getBrowserApiBaseUrl()}/api/export/payment/outstanding?${qs.toString()}`
}

export function countActiveFilters(
  p: BalanceReportListParams,
  lockedCompanyId?: string,
  lockedPropertyId?: string,
): number {
  let count = 0
  if (p.q.trim()) count++
  if (p.company_id && p.company_id !== lockedCompanyId) count++
  if (p.property_id && p.property_id !== lockedPropertyId) count++
  if (
    p.application_status &&
    p.application_status !== DEFAULT_BALANCE_REPORT_LIST_PARAMS.application_status
  )
    count++
  if (p.amount_condition || p.payment_amount) count++
  if (p.chargeback_type && p.chargeback_type !== DEFAULT_BALANCE_REPORT_LIST_PARAMS.chargeback_type)
    count++
  if (p.fromDt || p.toDt) count++
  return count
}

export function formatDisplayDate(value: string | undefined): string {
  return formatReportDate(value)
}

function toNumber(value: string | number | undefined): number {
  const n = Number(value ?? 0)
  return Number.isFinite(n) ? n : 0
}

export function paymentLogAmounts(log: OutstandingBalancePaymentLogRow) {
  const total = toNumber(log.payment_amount)
  const service = toNumber(log.payment_log_surcharge_fee ?? log.service_fee)
  const refundFee = toNumber(log.payment_log_refund_fee)
  const base =
    log.base_fee != null
      ? toNumber(log.base_fee)
      : service && service < total
        ? total - service
        : total + (refundFee ? refundFee * -1 : 0)
  return { base, service, total }
}

export function chargebackDisplay(
  value: OutstandingBalanceRow['chargeback_exist'],
): string {
  if (value === true) return 'Yes'
  if (
    value === false ||
    value == null ||
    value === '' ||
    value === 0 ||
    value === '0'
  )
    return 'No'
  return String(value)
}

export function padApplicantId(value: number | undefined): string {
  if (value == null) return ''
  return String(value).padStart(8, '0')
}

export function paymentLogDetailPath(
  log: OutstandingBalancePaymentLogRow,
): string | null {
  if (log.payment_log_id != null)
    return `/reports/payment-logs/${log.payment_log_id}`
  const adjustmentId = log.pa_id ?? log.payment_adjustment_id
  if (adjustmentId != null)
    return `/reports/payment-logs/detail/${adjustmentId}`
  return null
}

export function findContractPayment(
  application: OutstandingBalanceApplicationInfo,
): OutstandingBalancePaymentLogRow | null {
  return (
    application.payment_history?.find((payment) => {
      const paymentType = String(payment.payment_type ?? payment.type ?? '')
      return paymentType === 'CONTRACT'
    }) ?? null
  )
}

/**
 * Nested payment row edit visibility — Angular
 * `reports-outstanding-balance.component.html` edit button `*ngIf`.
 * Unlike the application detail tab, this template has no DEFAULT/DEFAULT_INVOICE clause.
 */
export function canEditBalanceNestedPayment(
  payment: OutstandingBalancePaymentLogRow,
  hasPolicy: (policyKeys: string[]) => boolean,
): boolean {
  if (!hasPolicy([NESTED_PAYMENT_EDIT_POLICY])) return false

  const paymentType = String(payment.payment_type ?? payment.type ?? '')
  const paymentSource = String(payment.payment_source ?? '').trim()

  if (NESTED_PAYMENT_EDIT_TYPES_WITH_SOURCE.includes(paymentType) && paymentSource) return true
  if (NESTED_PAYMENT_EDIT_TYPES_WITHOUT_SOURCE.includes(paymentType) && !paymentSource) return true
  if (
    NESTED_PAYMENT_REJECTED_REFUND_TYPES.includes(paymentType) &&
    payment.payment_status === NESTED_PAYMENT_STATUS_REJECTED
  )
    return true
  return false
}

/**
 * Nested payment row delete visibility — Angular
 * `reports-outstanding-balance.component.html` delete button `*ngIf`.
 */
export function canDeleteBalanceNestedPayment(
  payment: OutstandingBalancePaymentLogRow,
  hasPolicy: (policyKeys: string[]) => boolean,
): boolean {
  if (payment.payment_status === NESTED_PAYMENT_STATUS_CANCELLED) return false
  if (!hasPolicy([NESTED_PAYMENT_DELETE_POLICY])) return false

  const paymentType = String(payment.payment_type ?? payment.type ?? '')
  const paymentSource = payment.payment_source
  const sourceStr =
    paymentSource != null && paymentSource !== ''
      ? String(paymentSource)
      : ''

  if (
    NESTED_PAYMENT_DELETE_TYPES_WITHOUT_SOURCE.includes(paymentType) &&
    !paymentSource
  ) {
    return true
  }
  if (sourceStr && sourceStr.includes(NESTED_PAYMENT_EXTERNAL_SOURCE_MARKER)) {
    return true
  }
  if (
    !NESTED_PAYMENT_DELETE_EXCLUDED_INCOMPLETE_TYPES.includes(paymentType) &&
    payment.payment_status !== NESTED_PAYMENT_STATUS_COMPLETED
  ) {
    return true
  }
  return false
}
